import { useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { UserPlus, AlertCircle, ExternalLink, Trash2, Save, UserCheck, UserX, ListTodo, XCircle } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card';
import { Checkbox } from '~/components/ui/checkbox';
import { Label } from '~/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import { EnrollmentStatusBadge } from '~/components/enrollment';
import { useAddStudentToEnrollmentMutation } from './useAddStudentToEnrollmentMutation';
import { DeleteStudentEnrollmentMutation } from '../Enrollments/useDeleteStudentEnrollment';
import { UpdateStudentEnrollmentMutation } from '../Enrollments/useUpdateStudentEnrollment';
import { AcceptStudentEnrollmentMutation } from '../FamilyDetail/useAcceptStudentEnrollment';
import { WaitlistStudentEnrollmentMutation } from '../FamilyDetail/useWaitlistStudentEnrollment';
import { DenyStudentEnrollmentMutation } from '../FamilyDetail/useDenyStudentEnrollment';
import { WithdrawStudentEnrollmentMutation } from '../FamilyDetail/useWithdrawStudentEnrollment';
import type { VariablesOf } from 'gql.tada';
import { useSetStudentEnrollmentStatus, SetStudentEnrollmentStatusMutation } from './useSetStudentEnrollmentStatus';
import { StudentStatusMenu } from '../FamilyDetail/StudentStatusMenu';
import { getAvailableStudentEnrollmentActions, hasAvailableActions } from '~/utils/studentEnrollmentActions';
import type { PersonFromStudentDetail, StudentEnrollmentFromDetail, GradeLevelItem } from './types';
import type { SchoolYearRef } from '~/types';

type StatusVariable = VariablesOf<typeof SetStudentEnrollmentStatusMutation>['status'];

const STUDENT_ENROLLMENT_KINDS = [
  { value: 'NEW_STUDENT', label: 'New Student' },
  { value: 'RETURNING_STUDENT', label: 'Returning Student' },
];

interface EmptyEnrollmentStateProps {
  selectedYear: SchoolYearRef;
  personId: string;
  personName: string;
  familyId: string | undefined;
  onEnrollmentCreated?: () => void;
}

function EmptyEnrollmentState({
  selectedYear,
  personId,
  personName,
  familyId,
  onEnrollmentCreated
}: EmptyEnrollmentStateProps) {
  const [addStudent, { loading }] = useAddStudentToEnrollmentMutation();

  const handleAddToYear = async () => {
    if (!familyId) {
      console.error('No family ID available');
      return;
    }

    try {
      const result = await addStudent({
        variables: {
          input: {
            schoolYearId: selectedYear.id,
            familyId: familyId,
            personId: personId,
            enrollmentKind: 'NEW_STUDENT',
          },
        },
      });

      if (result.data?.addStudentToEnrollment?.studentEnrollment) {
        onEnrollmentCreated?.();
      }
    } catch (error) {
      console.error('Failed to add student to enrollment:', error);
    }
  };

  return (
    <Card className="border-dashed">
      <CardContent className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-2">
          No Enrollment for {selectedYear?.name}
        </h3>
        <p className="text-muted-foreground mb-6 max-w-md">
          {personName} is not enrolled for the {selectedYear?.name} school year yet.
        </p>
        <Button
          onClick={handleAddToYear}
          disabled={loading || !familyId}
        >
          <UserPlus className="h-4 w-4 mr-2" />
          {loading ? 'Adding...' : `Add to ${selectedYear?.name}`}
        </Button>
        {!familyId && (
          <p className="text-sm text-destructive mt-2">
            Unable to add: student is not associated with a family.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

interface StudentEnrollmentTabProps {
  person: PersonFromStudentDetail;
  enrollment: StudentEnrollmentFromDetail | null | undefined;
  selectedYear: SchoolYearRef | null;
  schoolSlug: string;
  familyId: string | undefined;
  gradeLevels?: GradeLevelItem[];
  // The school's checklist enrollment switch; the status menu waits for it.
  checklistEnrollmentOn?: boolean;
  onRefetch?: () => void;
}

export function StudentEnrollmentTab({
  person,
  enrollment,
  selectedYear,
  schoolSlug,
  familyId,
  gradeLevels = [],
  checklistEnrollmentOn = false,
  onRefetch,
}: StudentEnrollmentTabProps) {
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [actionConfirmDialog, setActionConfirmDialog] = useState<{ open: boolean; action: string | null }>({ open: false, action: null });
  const [editedData, setEditedData] = useState<Record<string, string | boolean | null>>({});
  const [isSaving, setIsSaving] = useState(false);

  const [deleteStudentEnrollment, { loading: deleting }] = useMutation(DeleteStudentEnrollmentMutation, {
    onCompleted: (result) => {
      if (result.deleteStudentEnrollment?.success) {
        setShowDeleteDialog(false);
        // If the family enrollment was also deleted, navigate back to families
        if (result.deleteStudentEnrollment.deletedFamilyEnrollment) {
          navigate(`/app/${schoolSlug}/families`);
        } else {
          onRefetch?.();
        }
      }
    },
  });

  const [updateStudentEnrollment] = useMutation(UpdateStudentEnrollmentMutation);

  // Student enrollment action mutations
  const [acceptStudentEnrollment, { loading: accepting }] = useMutation(AcceptStudentEnrollmentMutation, {
    onCompleted: () => onRefetch?.(),
  });
  const [waitlistStudentEnrollment, { loading: waitlisting }] = useMutation(WaitlistStudentEnrollmentMutation, {
    onCompleted: () => onRefetch?.(),
  });
  const [denyStudentEnrollment, { loading: denying }] = useMutation(DenyStudentEnrollmentMutation, {
    onCompleted: () => onRefetch?.(),
  });
  const [withdrawStudentEnrollment, { loading: withdrawing }] = useMutation(WithdrawStudentEnrollmentMutation, {
    onCompleted: () => onRefetch?.(),
  });

  const [setStudentEnrollmentStatus, { loading: settingStatus }] = useSetStudentEnrollmentStatus();

  const actionLoading = accepting || waitlisting || denying || withdrawing;

  // Check for changes
  const hasChanges = useMemo(() => {
    return Object.keys(editedData).length > 0;
  }, [editedData]);

  // Handle data change
  const handleDataChange = useCallback((field: string, value: string | boolean) => {
    setEditedData(prev => {
      const originalValue = enrollment?.[field as keyof StudentEnrollmentFromDetail];
      if (value === originalValue) {
        const { [field]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [field]: value };
    });
  }, [enrollment]);

  // Save changes (only callable when enrollment exists)
  const handleSaveChanges = useCallback(async () => {
    if (!enrollment) return;
    setIsSaving(true);
    try {
      await updateStudentEnrollment({
        variables: {
          studentEnrollmentId: enrollment.id,
          ...editedData
        }
      });
      setEditedData({});
      onRefetch?.();
    } catch (error) {
      console.error('Failed to save changes:', error);
    } finally {
      setIsSaving(false);
    }
  }, [editedData, enrollment, updateStudentEnrollment, onRefetch]);

  // Cancel changes
  const handleCancelChanges = useCallback(() => {
    setEditedData({});
  }, []);

  const handleDelete = () => {
    if (!enrollment) return;
    deleteStudentEnrollment({
      variables: { studentEnrollmentId: enrollment.id },
    });
  };

  // Student enrollment action handlers
  const handleAccept = () => {
    if (!enrollment) return;
    acceptStudentEnrollment({ variables: { studentEnrollmentId: enrollment.id } });
  };

  const handleWaitlist = () => {
    if (!enrollment) return;
    waitlistStudentEnrollment({ variables: { studentEnrollmentId: enrollment.id } });
  };

  const handleDeny = () => {
    setActionConfirmDialog({ open: true, action: 'deny' });
  };

  const handleWithdraw = () => {
    setActionConfirmDialog({ open: true, action: 'withdraw' });
  };

  const confirmAction = () => {
    if (!enrollment) return;
    const { action } = actionConfirmDialog;
    if (action === 'deny') {
      denyStudentEnrollment({ variables: { studentEnrollmentId: enrollment.id } });
    } else if (action === 'withdraw') {
      withdrawStudentEnrollment({ variables: { studentEnrollmentId: enrollment.id } });
    }
    setActionConfirmDialog({ open: false, action: null });
  };

  if (!selectedYear) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Select a school year to view enrollment information.
        </CardContent>
      </Card>
    );
  }

  if (!enrollment) {
    return (
      <EmptyEnrollmentState
        selectedYear={selectedYear}
        personId={person.id}
        personName={person.fullName}
        familyId={familyId}
        onEnrollmentCreated={onRefetch}
      />
    );
  }

  const familyEnrollment = enrollment.familyEnrollment;
  const canDelete = familyEnrollment?.access === 'NOT_INVITED';

  // Determine which actions are available based on state using shared utility
  const actions = getAvailableStudentEnrollmentActions(enrollment.state);
  const showActionsCard = hasAvailableActions(enrollment.state);

  return (
    <div className="space-y-6">
      {/* Floating save bar when there are changes */}
      {hasChanges && (
        <Card className="sticky top-0 z-10 border-primary bg-primary/5">
          <CardContent className="flex items-center justify-between py-3">
            <span className="text-sm font-medium">You have unsaved changes</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleCancelChanges} disabled={isSaving}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSaveChanges} disabled={isSaving}>
                <Save className="h-4 w-4 mr-1" />
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Enrollment Status Card */}
      <Card className={hasChanges ? 'ring-2 ring-primary' : ''}>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center justify-between">
            <span>Enrollment Status</span>
            <EnrollmentStatusBadge
              state={enrollment.state}
              type="student"
            />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Status</Label>
              <div className="h-8 flex items-center">
                <EnrollmentStatusBadge state={enrollment.state} type="student" />
              </div>
            </div>

            {checklistEnrollmentOn && (
              <StudentStatusMenu
                studentName={person.fullName}
                state={enrollment.state}
                saving={settingStatus}
                onConfirm={(status) => {
                  setStudentEnrollmentStatus({
                    variables: { studentEnrollmentId: enrollment.id, status: status as StatusVariable },
                    onCompleted: () => onRefetch?.(),
                  });
                }}
              />
            )}

            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Type</Label>
              <Select
                value={(editedData.enrollmentKind as string | undefined) ?? enrollment.enrollmentKind}
                onValueChange={(value) => handleDataChange('enrollmentKind', value)}
              >
                <SelectTrigger className="h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STUDENT_ENROLLMENT_KINDS.map((kind) => (
                    <SelectItem key={kind.value} value={kind.value}>
                      {kind.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Grade Level</Label>
              <Select
                value={(editedData.gradeLevel as string | undefined) ?? enrollment.gradeLevel ?? ''}
                onValueChange={(value) => handleDataChange('gradeLevel', value)}
              >
                <SelectTrigger className="h-8">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  {gradeLevels.map((grade) => (
                    <SelectItem key={grade.description} value={grade.description}>
                      {grade.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Needs Review</Label>
              <div className="flex items-center gap-2 h-8">
                <Checkbox
                  checked={(editedData.needsReview as boolean | undefined) ?? enrollment.needsReview ?? false}
                  onCheckedChange={(checked) => handleDataChange('needsReview', checked as boolean)}
                />
                <span className="text-sm">{(editedData.needsReview ?? enrollment.needsReview) ? 'Yes' : 'No'}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* State Actions Card */}
      {showActionsCard && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Enrollment Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {actions.canAccept && (
                <Button
                  onClick={handleAccept}
                  disabled={actionLoading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <UserCheck className="h-4 w-4 mr-2" />
                  {accepting ? 'Accepting...' : 'Accept'}
                </Button>
              )}
              {actions.canWaitlist && (
                <Button
                  variant="outline"
                  onClick={handleWaitlist}
                  disabled={actionLoading}
                  className="border-yellow-500 text-yellow-700 hover:bg-yellow-50"
                >
                  <ListTodo className="h-4 w-4 mr-2" />
                  {waitlisting ? 'Waitlisting...' : 'Waitlist'}
                </Button>
              )}
              {actions.canDeny && (
                <Button
                  variant="outline"
                  onClick={handleDeny}
                  disabled={actionLoading}
                  className="border-destructive text-destructive hover:bg-destructive/10"
                >
                  <UserX className="h-4 w-4 mr-2" />
                  {denying ? 'Denying...' : 'Deny'}
                </Button>
              )}
              {actions.canWithdraw && (
                <Button
                  variant="outline"
                  onClick={handleWithdraw}
                  disabled={actionLoading}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  {withdrawing ? 'Withdrawing...' : 'Withdraw'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Family Enrollment Link */}
      {familyEnrollment && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Family Enrollment</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">{familyEnrollment.family?.name}</p>
                <div className="flex items-center gap-2 mt-1">
                  <EnrollmentStatusBadge
                    state={familyEnrollment.state}
                    access={familyEnrollment.access}
                    accessSource={familyEnrollment.accessSource}
                    type="family"
                  />
                </div>
              </div>
              <Button variant="outline" size="sm" asChild>
                <Link to={`/app/${schoolSlug}/families/${familyEnrollment.family?.slug}/enrollment`}>
                  View Family Enrollment
                  <ExternalLink className="h-3 w-3 ml-1" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Remove from Year (only in draft) */}
      {canDelete && (
        <Card>
          <CardContent className="flex items-center justify-between py-4">
            <div>
              <p className="font-medium text-destructive">Remove from Year</p>
              <p className="text-sm text-muted-foreground">
                Remove {person.fullName} from {selectedYear.name} enrollment
              </p>
            </div>
            <Button variant="destructive" onClick={() => setShowDeleteDialog(true)}>
              <Trash2 className="h-4 w-4 mr-2" />
              Remove from Year
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Student from Year</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{person.fullName}</strong> from the{' '}
              {selectedYear.name} enrollment? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Removing...' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Action Confirmation Dialog (Deny/Withdraw) */}
      <AlertDialog
        open={actionConfirmDialog.open}
        onOpenChange={(open) => !open && setActionConfirmDialog({ open: false, action: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {actionConfirmDialog.action === 'deny' ? 'Deny Student Enrollment' : 'Withdraw Student'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {actionConfirmDialog.action === 'deny' ? (
                <>
                  Are you sure you want to deny <strong>{person.fullName}</strong>&apos;s enrollment?
                  This action cannot be undone.
                </>
              ) : (
                <>
                  Are you sure you want to withdraw <strong>{person.fullName}</strong>?
                  This will remove them from the enrollment.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmAction}
              disabled={denying || withdrawing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {denying || withdrawing
                ? (actionConfirmDialog.action === 'deny' ? 'Denying...' : 'Withdrawing...')
                : (actionConfirmDialog.action === 'deny' ? 'Deny Enrollment' : 'Withdraw Student')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default StudentEnrollmentTab;
